'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { Question } from './types';
import { QuizItem } from '@/store/assessment-store';
import { useAnswerHandlers } from './hooks/useAnswerHandlers';
import { QuestionCard } from './components/QuestionCard';
import { ChevronRightIcon } from '@/components/Icons/Icons';
import MaterialIcons from '../MaterialIcons/materialIcons';

interface AssessmentQuestionProps {
  question: Question;
  questionType: string | null;
  questionIndex: number;
  totalQuestions: number;
  quiz: QuizItem[];
  learningPathwayUID: string;
  setQuizData: (uid: string, data: QuizItem[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  submitButtonText?: string;
  isQuestionCardOpen?: boolean;
  isQuiz?: boolean;
  correctAnswers?: string | string[] | boolean | null;
  quizData?: any;
  onQuestionSubmit?: () => void;
  /** When provided (quiz flow), called when user clicks Submit Quiz – opens confirmation popup */
  onRequestSubmitQuiz?: () => void;
  /** When provided (assessment flow), called when user clicks Submit Assessment; caller can block opening popup if not all answered */
  onRequestSubmitAssessment?: () => void;
  /** When true, disables the Submit Assessment button (e.g. when not all questions are answered) */
  submitDisabled?: boolean;
  hideNavigation?: boolean; // Hide navigation footer in results mode
  showCorrectness?: boolean; // Show correct/wrong highlighting in results mode
  correctnessChecker?: { check: (option: string | boolean) => boolean | null }; // Function to check if option is correct
}

export function AssessmentQuestion({
  question,
  questionType,
  questionIndex,
  totalQuestions,
  quiz,
  learningPathwayUID,
  setQuizData,
  onNext,
  onPrevious,
  submitButtonText = 'Submit Assessment',
  isQuestionCardOpen = true,
  isQuiz = false,
  correctAnswers = null,
  quizData,
  onQuestionSubmit,
  onRequestSubmitQuiz,
  onRequestSubmitAssessment,
  submitDisabled = false,
  hideNavigation = false,
  showCorrectness = false,
  correctnessChecker,
}: AssessmentQuestionProps) {
  // Ensure quiz is always an array
  const quizArray = Array.isArray(quiz) ? quiz : [];
  
  const handlers = useAnswerHandlers({
    question,
    questionIndex,
    quiz: quizArray,
    learningPathwayUID,
    setQuizData,
  });

  if (!questionType) {
    return null;
  }

  const isFirstQuestion = questionIndex === 0;
  const isLastQuestion = questionIndex === totalQuestions - 1;
  const currentQuizItem = quizArray.find((q) => q.qindex === questionIndex);
  const isQuestionSubmitted = currentQuizItem?.isSubmitted || false;
  
  // Check if answer exists - handle boolean false as valid answer
  const selectedOptions = currentQuizItem?.selected_options;
  const hasAnswer = selectedOptions !== undefined && selectedOptions !== null && 
    (typeof selectedOptions === 'boolean' || 
     (typeof selectedOptions === 'string' && selectedOptions.length > 0) ||
     (Array.isArray(selectedOptions) && selectedOptions.length > 0));

  // In results mode (hideNavigation), use same structure but without toggle classes
  const outerDivClassName = hideNavigation 
    ? 'flex flex-col'
    : `bg-surface-1 rounded-lg p-8 flex flex-col gap-4 ${isQuestionCardOpen ? 'block' : 'hidden'}`;

  return (
    <div className={outerDivClassName}>
      <QuestionCard
        question={question}
        questionType={questionType}
        questionIndex={questionIndex}
        quiz={quiz}
        handlers={hideNavigation ? undefined : handlers} // Don't pass handlers in results mode
        containerClassName={hideNavigation ? 'bg-surface-1 rounded-lg p-8 flex flex-col gap-6' : 'flex flex-col gap-6'}
        innerContainerClassName="flex flex-col gap-6 bg-surface-2 rounded-lg p-8"
        isQuiz={isQuiz}
        correctAnswers={correctAnswers}
        quizData={quizData}
        isQuestionSubmitted={isQuestionSubmitted}
        showCorrectness={showCorrectness}
        correctnessChecker={correctnessChecker}
      />

      {/* Navigation Footer - Hidden in results mode */}
      {!hideNavigation && (
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2.5 p-8 bg-surface-2">
        <Button
          disabled={isFirstQuestion}
          type="button"
          variant="SecondaryFillButton"
          className="cs-button w-full sm:w-auto flex items-center justify-center gap-2"
          onClick={onPrevious}
        >
          Previous question
        </Button>
        <div className="flex flex-row flex-wrap items-center justify-end gap-2 w-full sm:w-auto min-w-0">
          {/* Quiz: Show Submit button for each question, Next Question only after submission */}
          {isQuiz ? (
            <>
              {isQuestionSubmitted && !isLastQuestion && (
                <Button
                  type="button"
                  className="cs-button w-full sm:w-auto flex items-center justify-center gap-2"
                  onClick={onNext}
                >
                  Next question
                </Button>
              )}
              {!isQuestionSubmitted && hasAnswer && (
                <Button
                  type="button"
                  className="cs-button w-full sm:w-auto flex items-center justify-center gap-2"
                  onClick={onQuestionSubmit}
                >
                  Submit
                </Button>
              )}
              {/* Don't show submit button on last question in results mode (when isQuestionSubmitted is always true) */}
              {isLastQuestion && isQuestionSubmitted && onQuestionSubmit && (
                <Button
                  type="button"
                  className="cs-button w-full sm:w-auto flex items-center justify-center gap-2"
                  onClick={() => {
                    if (onRequestSubmitQuiz) {
                      onRequestSubmitQuiz();
                    } else if (typeof window !== 'undefined') {
                      const event = new CustomEvent('toggle-popover', { detail: { id: 'submit-popup' } });
                      window.dispatchEvent(event);
                    }
                  }}
                >
                  {submitButtonText}
                  <MaterialIcons iconName="arrow_right_alt" className=" btn-icon !text-[20px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
                </Button>
              )}
              {/* Show Next button on last question if in review/results mode (no onQuestionSubmit) */}
              {isLastQuestion && isQuestionSubmitted && !onQuestionSubmit && (
                <Button
                  type="button"
                  className="cs-button w-full sm:w-auto flex items-center justify-center gap-2"
                  disabled
                >
                  Next question
                </Button>
              )}
            </>
          ) : (
            /* Assessment: Show Next Question or Submit Assessment on last question */
            <>
              {isLastQuestion ? (
                <Button
                  type="button"
                  className="cs-button w-full sm:w-auto flex items-center justify-center gap-2"
                  disabled={submitDisabled}
                  onClick={() => {
                    if (submitDisabled) return;
                    if (onRequestSubmitAssessment) {
                      onRequestSubmitAssessment();
                    } else if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('toggle-popover', { detail: { id: 'submit-popup' } }));
                    }
                  }}
                >
                  {submitButtonText}
                  <MaterialIcons iconName="arrow_right_alt" className=" btn-icon !text-[20px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1" />

                </Button>
              ) : (
                <Button
                  type="button"
                  className="cs-button w-full sm:w-auto flex items-center justify-center gap-2"
                  onClick={onNext}
                >
                  Next question
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

